// src/pages/OrderMenu2.js
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import {
  FaChevronRight,
  FaClock,
  FaInfoCircle,
  FaMoneyBillWave,
  FaStore,
  FaTimes,
} from 'react-icons/fa';
import { MdLocalOffer, MdRestaurantMenu } from 'react-icons/md';
import MainContent from '../components/Form/MainContent';
import FooterCart from '../components/Form/FooterCart';
import { useCart } from '../contexts/CartContext';
import { useStoreStatus } from '../contexts/useStoreStatus';
import { useTheme } from '../contexts/ThemeContext';
import { useApi } from '../hooks/useApi';
import { getCashBackPercent } from '../utils/cashBackUtils';
import { formatTime, getWeekdayName } from '../utils/dateTimeUtils';
import { formatCurrency } from '../utils/numberUtils';
import { createBase64ImageSource } from '../utils/imageUtils';
import './OrderMenu2.css';

Modal.setAppElement('#root');

const LOGO_SOURCES = ['/images/logo.png', '/images/logo.jpg', '/images/fallback.png'];

const GROUP_IMAGE_RULES = [
  { terms: ['novo', 'novidade'], source: '/images/novo.png' },
  { terms: ['acai'], source: '/images/acai.png' },
  { terms: ['crepe'], source: '/images/crepe.png' },
  { terms: ['xis'], source: '/images/xis.png' },
  { terms: ['hamburguer', 'burguer'], source: '/images/hamburguer2.png' },
  { terms: ['lanche'], source: '/images/lanche.png' },
  { terms: ['espetinho'], source: '/images/espetinho.png' },
  { terms: ['acompanhamento'], source: '/images/acompanhamentos.png' },
  { terms: ['picadao'], source: '/images/picadao.png' },
  { terms: ['promoc'], source: '/images/promocao.png' },
  { terms: ['combo'], source: '/images/combo.png' },
  { terms: ['baguete'], source: '/images/baguete.png' },
  { terms: ['porcao'], source: '/images/porcoes.png' },
  { terms: ['minuta'], source: '/images/laminuta.png' },
  { terms: ['kid'], source: '/images/pratokids.png' },
  { terms: ['pizza'], source: '/images/pizza.png' },
  { terms: ['bebida', 'refrigerante', 'suco'], source: '/images/bebidas.png' },
  { terms: ['bolo', 'pao'], source: '/images/bolopao.png' },
  { terms: ['prato quente'], source: '/images/pratosquentes.png' },
  { terms: ['salada'], source: '/images/salada.png' },
  { terms: ['guloseima'], source: '/images/guloseimas.png' },
  { terms: ['sobremesa'], source: '/images/sobremesa.png' },
];

const normalizeSearchText = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase();

const getGroupFallbackImage = (description) => {
  const normalizedDescription = normalizeSearchText(description);
  return GROUP_IMAGE_RULES.find((rule) => (
    rule.terms.some((term) => normalizedDescription.includes(term))
  ))?.source;
};

const getSectionItems = (result) => {
  if (Array.isArray(result)) {
    return result.filter((item) => item?.id);
  }

  const possibleCollections = [result?.produtos, result?.items, result?.itens];
  const collection = possibleCollections.find(Array.isArray);
  return collection ? collection.filter((item) => item?.id) : [];
};

const getSectionTitle = (result, fallbackTitle) => {
  const directTitle = result?.destaqueProduto?.titulo || result?.titulo;
  if (typeof directTitle === 'string' && directTitle.trim()) {
    return directTitle.trim();
  }

  if (Array.isArray(result)) {
    const itemWithTitle = result.find((item) => (
      item?.destaqueProduto?.titulo?.trim() || item?.titulo?.trim()
    ));
    const itemTitle = itemWithTitle?.destaqueProduto?.titulo || itemWithTitle?.titulo;
    if (itemTitle) {
      return itemTitle.trim();
    }
  }

  return fallbackTitle;
};

const buildSection = (key, result, fallbackTitle, useDynamicTitle = true) => {
  const items = getSectionItems(result);
  if (items.length === 0) return null;

  return {
    key,
    title: useDynamicTitle ? getSectionTitle(result, fallbackTitle) : fallbackTitle,
    items,
  };
};

const buildFeaturedSections = (result) => {
  const items = getSectionItems(result);
  const sections = new Map();

  items.forEach((item) => {
    const title = item?.destaqueProduto?.titulo?.trim() || 'Destaques';
    const key = `featured-products-${item?.destaqueProdutoId ?? title}`;

    if (!sections.has(key)) {
      sections.set(key, { key, title, items: [] });
    }

    sections.get(key).items.push(item);
  });

  return Array.from(sections.values());
};

const OrderMenu2 = () => {
  const navigate = useNavigate();
  const { fetchData } = useApi();
  const { removeCart } = useCart();
  const { corApp, corFonteApp, configModulo } = useTheme();
  const { isStoreOpen, msgStorageClose, horariosFuncionamento } = useStoreStatus();
  const fetchDataRef = useRef(fetchData);
  const loadingProductImagesRef = useRef(new Set());
  const loadingGroupImagesRef = useRef(new Set());

  const [groups, setGroups] = useState([]);
  const [productSections, setProductSections] = useState([]);
  const [appDesconto, setAppDesconto] = useState(0);
  const [cashBackPercent, setCashBackPercent] = useState(0);
  const [cashBackInfo, setCashBackInfo] = useState(null);
  const [logoIndex, setLogoIndex] = useState(0);
  const [deliveryLogoSrc, setDeliveryLogoSrc] = useState('');
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [isMenuLoading, setIsMenuLoading] = useState(true);
  const [groupsError, setGroupsError] = useState('');
  const [sectionsError, setSectionsError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  fetchDataRef.current = fetchData;

  useEffect(() => {
    const storedCartDate = localStorage.getItem('dataHoraCart');

    if (!storedCartDate) {
      removeCart();
      return;
    }

    const cartDate = new Date(storedCartDate);
    const cartAge = Date.now() - cartDate.getTime();
    const fourHours = 4 * 60 * 60 * 1000;

    if (Number.isNaN(cartDate.getTime()) || cartAge > fourHours) {
      removeCart();
    }
  }, []);

  useEffect(() => {
    let isActive = true;

    const loadMenu = async () => {
      setIsMenuLoading(true);
      setGroupsError('');
      setSectionsError('');
      setDeliveryLogoSrc('');
      setLogoIndex(0);
      loadingProductImagesRef.current.clear();
      loadingGroupImagesRef.current.clear();

      const [groupsResult, configResult, featuredResult, mostConsumedResult] = await Promise.allSettled([
        fetchDataRef.current('/GrupoDelivery'),
        fetchDataRef.current('/ConfigPedido'),
        fetchDataRef.current('/ProdutoDelivery/ProdutosDestaque'),
        fetchDataRef.current('/ProdutoDelivery/ProdutosMaisConsumidos'),
      ]);

      if (!isActive) return;

      if (groupsResult.status === 'fulfilled') {
        setGroups(Array.isArray(groupsResult.value) ? groupsResult.value : []);
      } else {
        setGroups([]);
        setGroupsError('Não foi possível carregar as categorias agora.');
      }

      const featuredSections = featuredResult.status === 'fulfilled'
        ? buildFeaturedSections(featuredResult.value)
        : [];
      const mostConsumedSection = mostConsumedResult.status === 'fulfilled'
        ? buildSection('most-consumed-products', mostConsumedResult.value, 'Os mais pedidos', false)
        : null;

      setProductSections([...featuredSections, mostConsumedSection].filter(Boolean));

      if (featuredResult.status === 'rejected' && mostConsumedResult.status === 'rejected') {
        setSectionsError('Não foi possível carregar as sugestões agora.');
      }

      if (configResult.status === 'fulfilled') {
        const config = configResult.value || {};
        setAppDesconto(Number(config.appDesconto) || 0);
        setCashBackPercent(getCashBackPercent(config));

        const filialId = Number(config.filialId || configModulo?.filialId);
        if (filialId > 0) {
          try {
            const logoResult = await fetchDataRef.current(`/Filial/${filialId}/logo-delivery`);
            if (!isActive) return;

            setDeliveryLogoSrc(createBase64ImageSource(
              logoResult?.imagemBase64,
              logoResult?.mimeType,
            ));
          } catch {
            // Mantém a logo local para instalações que ainda não cadastraram a identidade do delivery.
          }
        }
      } else {
        setAppDesconto(0);
        setCashBackPercent(0);
      }

      setIsMenuLoading(false);

      const telefone = localStorage.getItem('telefoneClienteInput');
      if (!telefone) {
        setCashBackInfo(null);
        return;
      }

      try {
        const cashBackResult = await fetchDataRef.current('/CashBack/Saldo', { telefone });
        if (!isActive) return;

        setCashBackInfo(cashBackResult);
        const resultPercent = getCashBackPercent(cashBackResult);
        if (resultPercent > 0) {
          setCashBackPercent(resultPercent);
        }
      } catch {
        if (isActive) setCashBackInfo(null);
      }
    };

    loadMenu();

    return () => {
      isActive = false;
    };
  }, [reloadKey]);

  useEffect(() => {
    productSections
      .flatMap((section) => section.items)
      .filter((item) => !item.imagemBase64 && !loadingProductImagesRef.current.has(item.id))
      .forEach((item) => {
        loadingProductImagesRef.current.add(item.id);

        fetchDataRef.current('/ProdutoDelivery/ProdutoImagem', { produtoId: item.id })
          .then((result) => {
            if (!result?.imagemBase64) return;

            setProductSections((currentSections) => currentSections.map((section) => ({
              ...section,
              items: section.items.map((product) => (
                product.id === item.id
                  ? { ...product, imagemBase64: result.imagemBase64 }
                  : product
              )),
            })));
          })
          .catch(() => {
            // Mantém a imagem padrão quando a imagem do produto não estiver disponível.
          });
      });
  }, [productSections]);

  useEffect(() => {
    groups
      .filter((group) => !group.imagemBase64 && !loadingGroupImagesRef.current.has(group.id))
      .forEach((group) => {
        loadingGroupImagesRef.current.add(group.id);

        fetchDataRef.current('/GrupoDelivery/GrupoImagem', { grupoId: group.id })
          .then((result) => {
            if (!result?.imagemBase64) return;

            setGroups((currentGroups) => currentGroups.map((currentGroup) => (
              currentGroup.id === group.id
                ? { ...currentGroup, imagemBase64: result.imagemBase64, nomeImagem: result.nome }
                : currentGroup
            )));
          })
          .catch(() => {
            // Usa a ilustração local quando o grupo não possuir imagem cadastrada.
          });
      });
  }, [groups]);

  const formattedHours = useMemo(() => (
    [...(horariosFuncionamento || [])]
      .sort((firstDay, secondDay) => firstDay.diaSemana - secondDay.diaSemana)
      .map((day) => ({
        ...day,
        dayName: getWeekdayName(day.diaSemana),
      }))
  ), [horariosFuncionamento]);

  const goToMenu = (group) => {
    if (group.temSabor === 1) {
      navigate(`/tipoPizza/${group.id}`);
      return;
    }

    navigate(`/itensmenu/${group.id}`, { state: { group } });
  };

  const goToProduct = (item) => {
    if (!item.esgotado) {
      navigate(`/itemdetails/${item.id}`, { state: { itemSel: item } });
    }
  };

  const handleLogoError = () => {
    if (deliveryLogoSrc) {
      setDeliveryLogoSrc('');
      setLogoIndex(0);
      return;
    }

    setLogoIndex((currentIndex) => Math.min(currentIndex + 1, LOGO_SOURCES.length - 1));
  };

  const getGroupCashBackPercent = (group) => {
    const groupPercent = getCashBackPercent(group);
    return groupPercent > 0 ? groupPercent : cashBackPercent;
  };

  const pageStyle = {
    '--order-menu2-theme-color': corApp,
    '--order-menu2-theme-text': corFonteApp || '#ffffff',
  };
  const storeName = configModulo?.nomeEstabelecimento?.trim() || 'Nosso cardápio';

  return (
    <>
      <MainContent className="order-menu2-main">
        <div className="order-menu2-page" style={pageStyle}>
          <section
            className="order-menu2-hero"
            aria-labelledby="order-menu2-store-name"
          >
            <div className="order-menu2-brand-row">
              <div className="order-menu2-logo-wrapper">
                <img
                  src={deliveryLogoSrc || LOGO_SOURCES[logoIndex]}
                  alt={`Logo de ${storeName}`}
                  className="order-menu2-logo"
                  onError={handleLogoError}
                />
              </div>
              <div className="order-menu2-hero-content">
                <p className="order-menu2-eyebrow">
                  <FaStore aria-hidden="true" />
                  Cardápio digital
                </p>
                <h1 id="order-menu2-store-name">{storeName}</h1>
                <p className="order-menu2-welcome">O que você gostaria de pedir?</p>
              </div>
            </div>
            {!isStoreOpen && typeof isStoreOpen === 'boolean' && (
              <p className="order-menu2-closed-message" role="status">
                {msgStorageClose || 'Estabelecimento fechado no momento.'}
              </p>
            )}
            <div className="order-menu2-hero-actions">
                <span
                  className={`order-menu2-store-status ${
                    typeof isStoreOpen !== 'boolean'
                      ? 'is-loading'
                      : isStoreOpen ? 'is-open' : 'is-closed'
                  }`}
                  aria-live="polite"
                >
                  <span className="order-menu2-store-status-dot" aria-hidden="true" />
                  {typeof isStoreOpen !== 'boolean'
                    ? 'Verificando'
                    : isStoreOpen ? 'Aberto agora' : 'Fechado'}
                </span>
                <button
                  type="button"
                  className="order-menu2-hours-button"
                  onClick={() => setModalIsOpen(true)}
                >
                  <FaClock aria-hidden="true" />
                  <span>Ver horários</span>
                </button>
            </div>
          </section>

          <div className="order-menu2-promotions" aria-label="Benefícios do pedido pelo aplicativo">
            {appDesconto > 0 && (
              <div className="order-menu2-promotion order-menu2-promotion-discount">
                <MdLocalOffer aria-hidden="true" />
                <span>Pedidos pelo app têm <strong>{appDesconto}% de desconto</strong></span>
              </div>
            )}

            {cashBackInfo?.saldo > 0 ? (
              <div className="order-menu2-promotion order-menu2-promotion-cashback">
                <FaMoneyBillWave aria-hidden="true" />
                <span>Você tem <strong>{formatCurrency(cashBackInfo.saldo)}</strong> de cashback</span>
              </div>
            ) : cashBackPercent > 0 ? (
              <div className="order-menu2-promotion order-menu2-promotion-cashback">
                <FaMoneyBillWave aria-hidden="true" />
                <span>Ganhe <strong>{cashBackPercent}% de cashback</strong> em suas compras</span>
              </div>
            ) : null}
          </div>

          {isMenuLoading && (
            <section className="order-menu2-loading-section" aria-label="Carregando cardápio" aria-busy="true">
              <div className="order-menu2-loading-title" />
              <div className="order-menu2-loading-row">
                {[1, 2, 3].map((item) => <div key={item} className="order-menu2-product-skeleton" />)}
              </div>
            </section>
          )}

          {!isMenuLoading && productSections.length > 0 && (
            <div className="order-menu2-product-sections">
              {productSections.map((section) => (
                <section key={section.key} className="order-menu2-product-section">
                  <div className="order-menu2-section-heading">
                    <div>
                      <span>Selecionados para você</span>
                      <h2>{section.title}</h2>
                    </div>
                  </div>
                  <div className="order-menu2-product-list">
                    {section.items.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        className={`order-menu2-product-card ${item.esgotado ? 'is-sold-out' : ''}`}
                        onClick={() => goToProduct(item)}
                        disabled={Boolean(item.esgotado)}
                      >
                        <div className="order-menu2-product-image-wrapper">
                          <img
                            src={item.imagemBase64
                              ? createBase64ImageSource(item.imagemBase64)
                              : '/images/image_default.jpg'}
                            alt={item.descricao || 'Produto'}
                            className="order-menu2-product-image"
                            loading="lazy"
                          />
                          {item.esgotado && <span className="order-menu2-sold-out-label">Esgotado</span>}
                        </div>
                        <span className="order-menu2-product-name">{item.descricao}</span>
                        <span className="order-menu2-product-price">{formatCurrency(item.preco)}</span>
                      </button>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}

          {!isMenuLoading && sectionsError && productSections.length === 0 && (
            <p className="order-menu2-inline-message">
              <FaInfoCircle aria-hidden="true" />
              {sectionsError}
            </p>
          )}

          <section className="order-menu2-categories" aria-labelledby="order-menu2-categories-title">
            <div className="order-menu2-section-heading">
              <div>
                <span>Explore o cardápio</span>
                <h2 id="order-menu2-categories-title">Categorias</h2>
              </div>
            </div>

            {isMenuLoading ? (
              <div className="order-menu2-category-grid" aria-hidden="true">
                {[1, 2, 3, 4].map((item) => <div key={item} className="order-menu2-category-skeleton" />)}
              </div>
            ) : groupsError ? (
              <div className="order-menu2-error-state" role="alert">
                <MdRestaurantMenu aria-hidden="true" />
                <p>{groupsError}</p>
                <button type="button" onClick={() => setReloadKey((currentKey) => currentKey + 1)}>
                  Tentar novamente
                </button>
              </div>
            ) : groups.length > 0 ? (
              <div className="order-menu2-category-grid">
                {groups.map((group) => {
                  const groupPercent = getGroupCashBackPercent(group);
                  const fallbackImage = getGroupFallbackImage(group.descricao);

                  return (
                    <button
                      key={group.id}
                      type="button"
                      className="order-menu2-category-card"
                      onClick={() => goToMenu(group)}
                    >
                      <span className="order-menu2-category-visual">
                        {group.imagemBase64 ? (
                          <img
                            src={createBase64ImageSource(group.imagemBase64)}
                            alt=""
                            loading="lazy"
                          />
                        ) : fallbackImage ? (
                          <img src={fallbackImage} alt="" loading="lazy" />
                        ) : (
                          <MdRestaurantMenu aria-hidden="true" />
                        )}
                      </span>
                      <span className="order-menu2-category-name">{group.descricao}</span>
                      {groupPercent > 0 && (
                        <span className="order-menu2-category-cashback">{groupPercent}% cashback</span>
                      )}
                      <span className="order-menu2-category-action">
                        Ver itens
                        <FaChevronRight aria-hidden="true" />
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="order-menu2-empty-state">Nenhuma categoria disponível no momento.</p>
            )}
          </section>
        </div>
      </MainContent>

      <FooterCart />

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={() => setModalIsOpen(false)}
        className="order-menu2-hours-modal"
        overlayClassName="order-menu2-hours-overlay"
        closeTimeoutMS={150}
        contentLabel="Horários de atendimento"
      >
        <div className="order-menu2-modal-handle" aria-hidden="true" />
        <div className="order-menu2-modal-header">
          <div>
            <span>Confira quando estamos disponíveis</span>
            <h2>Horários de atendimento</h2>
          </div>
          <button
            type="button"
            className="order-menu2-modal-close"
            onClick={() => setModalIsOpen(false)}
            aria-label="Fechar horários de atendimento"
          >
            <FaTimes aria-hidden="true" />
          </button>
        </div>

        {formattedHours.length > 0 ? (
          <div className="order-menu2-hours-list">
            {formattedHours.map((day) => (
              <div key={day.diaSemana} className="order-menu2-hours-row">
                <strong>{day.dayName}</strong>
                <div>
                  {day.periodos?.length > 0 ? day.periodos.map((period, index) => (
                    <span key={`${period.horaAbertura}-${period.horaFechamento}-${index}`}>
                      {formatTime(period.horaAbertura)} às {formatTime(period.horaFechamento)}
                    </span>
                  )) : <span>Fechado</span>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="order-menu2-hours-empty">Horários indisponíveis no momento.</p>
        )}
      </Modal>
    </>
  );
};

export default OrderMenu2;
